import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { propOr, mergeRight } from 'ramda';

import { ReportContext } from './Report.react'
import { templateLiteralDedent } from '../utils';

/**
 * An app-level header component.
 *
 * **Example Usage**
 * ```
 * ddk.Header([
 *     ddk.Title('FRED Economic Indicators'),
 *     ddk.Logo(src='/assets/my-logo.png'),
 *     ddk.Menu([
 *         ddk.CollapsibleMenu(
 *             title='Monetary Data',
 *             children=[
 *                 dcc.Link(
 *                 'Monetary Base',
 *                 href='/monetary-base'
 *             ),
 *             dcc.Link(
 *                 'Money Velocity',
 *                 href='/money-velocity'
 *             ),
 *             dcc.Link(
 *                 'Reserves',
 *                 href='/reserves'
 *             ),
 *             dcc.Link(
 *                 'Borrowings',
 *                 href='/borrowings'
 *             )
 *         ]),
 *         dcc.Link('Conditions', href='/conditions'),
 *         dcc.Link('Investment', href='/investments'),
 *         dcc.Link('Other', href='/other'),
 *     ])
 * ])
 * ```
 */
function Header(props) {

    const {
        margin,
        padding,
        background_color,
        border_width,
        border_style,
        border_color,
        border_radius,
        box_shadow,
    } = props;

    /* merge conditional styles from props */
    const style = mergeRight(
        {
            padding: padding,
            margin: margin,
            backgroundColor: background_color,
            borderWidth: border_width,
            borderStyle: border_style,
            borderColor: border_color,
            borderRadius: border_radius,
            boxShadow: box_shadow,
        },
        propOr({}, 'style', props)
    );
    return (
        <ReportContext.Consumer>
            { context => {
                if (context) {
                    const { inReportContext, suppress_layout_exceptions } = context
                    if (inReportContext && !suppress_layout_exceptions) {
                        throw new Error(
                            templateLiteralDedent(`
                                You added a ddk.Header within
                                a ddk.Report, but
                                it did not render because
                                we strongly recommend using
                                ddk.PageHeader instead, which was
                                designed for Pages within Reports.
                                By default, ddk.PageHeaders appear in the top
                                margin of your ddk.Page and should
                                be its first child.

                                If you wish to render the ddk.Header
                                anyway, add suppress_layout_exceptions=True
                                to your ddk.Report.
                            `)
                        )
                    }
                }

                return (
                    <header
                        className={`layout-header ${propOr('', 'className', props)}`}
                        style={style}
                        {...props}
                    >
                        {props.children}
                    </header>
                )
            }}
        </ReportContext.Consumer>
    );
}

Header.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * The contents of the Header.
     * This is frequently a list containing
     * a `ddk.Logo`, a `ddk.Title`, and a `ddk.Menu`:
     * ```
     * [
     *     ddk.Logo(src='/assets/logo.png'),
     *     ddk.Title('Header Title'),
     *     ddk.Menu([
     *         dcc.Link('Historical', href='/historical'),
     *         dcc.Link('Forecast', href='/forecast')
     *     ]),
     * ]
     * ```
     * but it can also contain arbitrary components
     * like controls or buttons.
     */
    children: PropTypes.node,

    /**
     * Space (in pixels) surrounding the header.
     * Overrides theme.header_margin.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the header, between the border
     * and the edge of the content.
     * Overrides theme.header_padding.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the header. Overrides theme.header_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the header. Overrides theme.header_box_shadow.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the header. Overrides theme.header_border.width.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the header. Overrides theme.header_border.style.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the header. Overrides theme.header_border.color.
     */
    border_color: PropTypes.string,

    /**
     * The border radius applied to the header. Overrides theme.header_border.radius.
     */
    border_radius: PropTypes.string,

    /**
     * The style object of the outermost div of the Sidebar.
     * Use this to override the Sidebar's height
     * (`{'height': '100px'}`) or the background color
     * (`{'backgroundColor': 'white'}`)
     */
    style: PropTypes.object
}

export default Header;
